import { GlTokenSelector, GlAlert } from '/builds/gitlab/gitlab-ee/node_modules/@gitlab/ui/src/index.js?vue3';
import { debounce, escape } from 'lodash-es';
import { isNumeric } from '~/lib/utils/number_utils';
import { DEFAULT_DEBOUNCE_AND_THROTTLE_MS } from '~/lib/utils/constants';
import { sprintf } from '~/locale';
import SafeHtml from '~/vue_shared/directives/safe_html';
import { titleInLinkSafeHtmlConfig } from '~/lib/dompurify';
import { isValidURL } from '~/lib/utils/url_utility';
import { highlighter } from '/builds/gitlab/gitlab-ee/ee/app/assets/javascripts/gfm_auto_complete.js?vue3';
import workItemAncestorsQuery from '../../graphql/work_item_ancestors.query.graphql';
import groupWorkItemsQuery from '../../graphql/group_work_items.query.graphql';
import projectWorkItemsQuery from '../../graphql/project_work_items.query.graphql';
import workItemsByReferencesQuery from '../../graphql/work_items_by_references.query.graphql';
import { I18N_WORK_ITEM_SEARCH_ERROR } from '../../constants';
import { formatAncestors, isReference } from '/builds/gitlab/gitlab-ee/app/assets/javascripts/work_items/utils.js?vue3';
export default {
    name: 'WorkItemTokenInput',
    components: {
        GlTokenSelector,
        GlAlert
    },
    directives: {
        SafeHtml
    },
    props: {
        value: {
            type: Array,
            required: false,
            default: ()=>[]
        },
        fullPath: {
            type: String,
            required: true
        },
        isGroup: {
            type: Boolean,
            required: false,
            default: false
        },
        childrenType: {
            type: Object,
            required: false,
            default: ()=>({})
        },
        childrenIds: {
            type: Array,
            required: false,
            default: ()=>[]
        },
        parentWorkItemId: {
            type: String,
            required: true
        },
        areWorkItemsToAddValid: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    emits: [
        'input',
        'searching'
    ],
    apollo: {
        namespaceWorkItems: {
            query () {
                return this.isGroup ? groupWorkItemsQuery : projectWorkItemsQuery;
            },
            variables () {
                return this.queryVariables;
            },
            skip () {
                return !this.searchStarted || this.isSearchingByReference;
            },
            update (data) {
                return [
                    ...this.filterItems(data.namespace?.workItemsByIid?.nodes || []),
                    ...this.filterItems(data.namespace?.workItems?.nodes || [])
                ];
            },
            error () {
                this.error = sprintf(I18N_WORK_ITEM_SEARCH_ERROR, {
                    workItemType: this.childrenType.name
                });
            }
        },
        workItemsByReference: {
            query: workItemsByReferencesQuery,
            variables () {
                return {
                    contextNamespacePath: this.fullPath,
                    refs: [
                        this.searchTerm
                    ]
                };
            },
            skip () {
                return !this.isSearchingByReference;
            },
            update (data) {
                return this.filterItems(data.workItemsByReference.nodes);
            },
            error () {
                this.error = sprintf(I18N_WORK_ITEM_SEARCH_ERROR, {
                    workItemType: this.childrenType.name
                });
            }
        },
        ancestorIds: {
            query: workItemAncestorsQuery,
            variables () {
                return {
                    id: this.parentWorkItemId
                };
            },
            update (data) {
                return formatAncestors(data.workItem).flatMap((ancestor)=>[
                        ancestor.id
                    ]);
            },
            skip () {
                return !this.parentWorkItemId;
            }
        }
    },
    data () {
        return {
            ancestorIds: [],
            namespaceWorkItems: [],
            workItemsByReference: [],
            searchTerm: '',
            searchStarted: false,
            error: '',
            textInputAttrs: {
                class: '!gl-min-w-fit'
            }
        };
    },
    computed: {
        availableWorkItems () {
            return this.isSearchingByReference ? this.workItemsByReference : this.namespaceWorkItems;
        },
        isSearchingByReference () {
            return isReference(this.searchTerm) || isValidURL(this.searchTerm);
        },
        workItemsToAdd: {
            get () {
                return this.value;
            },
            set (workItemsToAdd) {
                this.$emit('input', workItemsToAdd);
            }
        },
        isLoading () {
            return this.$apollo.queries.namespaceWorkItems.loading || this.$apollo.queries.workItemsByReference.loading;
        },
        tokenSelectorContainerClass () {
            return !this.areWorkItemsToAddValid ? '!gl-shadow-inner-1-red-500' : '';
        },
        queryVariables () {
            const variables = {
                fullPath: this.fullPath,
                searchTerm: this.searchTerm,
                workItemTypeIds: this.childrenType.id ? [
                    this.childrenType.id
                ] : [],
                in: this.searchTerm ? 'TITLE' : undefined,
                iid: isNumeric(this.searchTerm) ? this.searchTerm : null,
                searchByIid: isNumeric(this.searchTerm),
                searchByText: true
            };
            if (this.isGroup) {
                variables.includeAncestors = true;
                variables.includeDescendants = true;
            }
            return variables;
        }
    },
    created () {
        this.debouncedSearchKeyUpdate = debounce(this.setSearchKey, DEFAULT_DEBOUNCE_AND_THROTTLE_MS);
    },
    methods: {
        async setSearchKey (value) {
            this.searchTerm = value;
        },
        handleFocus () {
            this.searchStarted = true;
            this.$emit('searching', true);
        },
        handleMouseOver () {
            this.timeout = setTimeout(()=>{
                this.searchStarted = true;
            }, DEFAULT_DEBOUNCE_AND_THROTTLE_MS);
        },
        handleMouseOut () {
            clearTimeout(this.timeout);
        },
        handleBlur () {
            this.$emit('searching', false);
        },
        focusInputText () {
            this.$nextTick(()=>{
                if (this.areWorkItemsToAddValid) {
                    this.$refs.tokenSelector.focusTextInput();
                }
            });
        },
        formatResults (input) {
            const escapedInput = escape(input);
            if (!this.searchTerm) {
                return escapedInput;
            }
            return highlighter(`<span class="gl-text-default">${escapedInput}</span>`, this.searchTerm);
        },
        unsetError () {
            this.error = '';
        },
        filterItems (items) {
            return items?.filter((wi)=>{
                return !this.childrenIds.includes(wi.id) && this.parentWorkItemId !== wi.id && !this.ancestorIds.includes(wi.id);
            }) || [];
        }
    },
    titleInLinkSafeHtmlConfig
};
